// Helper para Mercado Pago SDK (frontend tokenization).
//
// Carga MP.js dinamicamente, tokeniza la tarjeta del cliente y devuelve el token.
// El token despues se manda a la edge function mp-create-subscription que se
// encarga de crear customer + card + preapproval en MP.
//
// Docs: https://www.mercadopago.com.uy/developers/es/docs/checkout-api/integration-configuration/card/integration-via-cardform

import { supabase } from './supabase'

const MP_PUBLIC_KEY = import.meta.env.VITE_MP_PUBLIC_KEY

let mpInstance = null
let mpScriptLoading = null

/** Carga MP SDK (singleton). */
export async function cargarMP() {
  if (mpInstance) return mpInstance
  if (mpScriptLoading) return mpScriptLoading

  if (!MP_PUBLIC_KEY) {
    throw new Error('Falta VITE_MP_PUBLIC_KEY en .env')
  }

  mpScriptLoading = new Promise((resolve, reject) => {
    // Si ya esta cargado el script
    if (window.MercadoPago) {
      mpInstance = new window.MercadoPago(MP_PUBLIC_KEY, { locale: 'es-UY' })
      return resolve(mpInstance)
    }
    const script = document.createElement('script')
    script.src = 'https://sdk.mercadopago.com/js/v2'
    script.onload = () => {
      mpInstance = new window.MercadoPago(MP_PUBLIC_KEY, { locale: 'es-UY' })
      resolve(mpInstance)
    }
    script.onerror = () => reject(new Error('No se pudo cargar Mercado Pago SDK'))
    document.head.appendChild(script)
  })

  return mpScriptLoading
}

/**
 * Tokeniza una tarjeta y devuelve el card_token.
 *
 * @param {Object} datos
 * @param {string} datos.cardNumber       - "4509 9535 6623 3704"
 * @param {string} datos.cardholderName   - "APRO" para test approved
 * @param {string} datos.cardExpirationMonth - "11"
 * @param {string} datos.cardExpirationYear  - "2030"
 * @param {string} datos.securityCode     - "123"
 * @param {string} datos.identificationType - "CI" | "DNI" (depende del pais)
 * @param {string} datos.identificationNumber - "12345678"
 *
 * @returns {Promise<{ id: string, last_four_digits: string, payment_method_id: string }>}
 */
export async function tokenizarTarjeta(datos) {
  const mp = await cargarMP()
  const token = await mp.createCardToken({
    cardNumber:               datos.cardNumber.replace(/\s/g, ''),
    cardholderName:           datos.cardholderName,
    cardExpirationMonth:      datos.cardExpirationMonth,
    cardExpirationYear:       datos.cardExpirationYear,
    securityCode:             datos.securityCode,
    identificationType:       datos.identificationType || 'CI',
    identificationNumber:     datos.identificationNumber,
  })
  if (!token?.id) throw new Error('No se pudo tokenizar la tarjeta')
  return token
}

/**
 * Crea la suscripcion en MP llamando a la edge function.
 * El cliente debe estar logueado (usa JWT actual).
 *
 * @param {Object} p
 * @param {string} p.clienteId   - id de la suscripcion (tabla clientes)
 * @param {string} p.cardToken   - token devuelto por tokenizarTarjeta
 * @param {string} p.email
 * @param {number} p.monto       - precio mensual del kit
 * @param {string} [p.kitCodigo]
 *
 * @returns {Promise<{ ok: boolean, mp_card_last4: string, mp_card_brand: string, mp_preapproval_id: string }>}
 */
export async function crearSuscripcionMP({ clienteId, cardToken, email, monto, kitCodigo }) {
  const { data, error } = await supabase.functions.invoke('mp-create-subscription', {
    body: {
      cliente_id: clienteId,
      card_token: cardToken,
      email,
      monto,
      kit_codigo: kitCodigo,
    },
  })
  if (error) throw error
  if (data?.error) throw new Error(data.error + (data.detalle ? ': ' + JSON.stringify(data.detalle) : ''))
  return data
}
